import EventEmitter from "events";
import { TSAPIService } from "../services/mvcapi";
import { PaymentInfoModel, PrepareStandByOrderResponseModel, prepareStandbyOrder, processTransaction, PaymentInfoModelMethodEnum, ThreeDSDeviceData, process3DSAuthentication, Process3DSAuthenticationResultValue, Process3DSAuthenticationResult } from "../services/orderprocessing";
import { OrderProcessingResult } from "../mvcapi/models/transactionprocessing/orderprocessingresult";
import TranslatorManager from 'ts-translate-js';

const tr = TranslatorManager.boundTR;

export const RESERVATECH_3DS_DONE = "RESERVATECH_3DS_DONE";
export const RESERVATECH_3DS_OK = "RESERVATECH_3DS_OK";
export const RESERVATECH_3DS_FAIL = "RESERVATECH_3DS_FAIL";

export const THREEDS_METHOD_CHECK_TIMEOUT = 3 * 1000;

export class PaymentTool extends EventEmitter {
    mvcapi: TSAPIService;

    private resolveProcess: (value: any) => void
    private rejectProcess: (value: any) => void
    
    // ************************************************************
    // Process
    // ************************************************************
    private firstTransactionResult: OrderProcessingResult
    private finalPmtResult: OrderProcessingResult
    

    private threeDSMethodTimeout: NodeJS.Timeout | null = null;
    private threeDSMethodURLDone: boolean = false;

    private threeDSAuthResult: Process3DSAuthenticationResult = null;
    private threeDSChallengeDone: boolean = false;

    private sendMailAtTheEnd: boolean = false;


    // ************************************************************
    // Events
    // ************************************************************
    // 3DS Device Gathering Hidden Iframe
    on3DSHiddenIFrameNeeded: () => Promise<HTMLIFrameElement>; // Return IFrame Name
    on3DSHiddenIFrameDone: () => void;

    // 3DS Challenge Iframe
    on3DSChallengeIFrameNeeded: (width: number, height: number) => Promise<HTMLIFrameElement>;
    on3DSChallengeIFrameDone: () => void;

    // Outcome
    onPaymentSuccess: () => void;
    onPaymentFailed: (message: string) => void;



    // ************************************************************
    // Operations
    // ************************************************************

    constructor(mvcapi: TSAPIService) {
        super();
        this.mvcapi = mvcapi;
    }


    /**
     * Process an internet sale regular transaction (Prepare standby + Payment)
     */
    async processSaleTransaction(cartTotalValidation: number, shippingID: string, paymentMethod: PaymentInfoModel, sendEmail: boolean): Promise<ProcessSaleTransactionResult> {
        this.sendMailAtTheEnd = sendEmail;
        const resultPromise = new Promise<ProcessSaleTransactionResult>((resolve, reject) => {
            this.resolveProcess = resolve;
            this.rejectProcess = reject;
        });

        try {

            // Execute prepare standby order
            let standbyOrderResult: PrepareStandByOrderResponseModel;
            try {
                standbyOrderResult = await prepareStandbyOrder(this.mvcapi, cartTotalValidation, shippingID);
            } catch (err) {
                const res: ProcessSaleTransactionResult = {
                    isSuccess: false,
                    step: "standby",
                    prepareStandbyOrderResult: err as PrepareStandByOrderResponseModel
                } as ProcessSaleTransactionResult;
                throw res;
            }
            
            // Execute the payment
            // Si jamais le cart est a zero on s'assure que cest bien un prepareResult a noPaymentNeeded
            const actualPaymentMethod = standbyOrderResult.totalWithoutPrepaidCardAmount == 0 && standbyOrderResult.noPaymentNeeded 
                ? { method : PaymentInfoModelMethodEnum.NoPayment } 
                : paymentMethod;
            
            
            try {
                this.firstTransactionResult = await processTransaction(this.mvcapi, standbyOrderResult.encryptedUID, actualPaymentMethod, this.sendMailAtTheEnd);
            } catch(err) {
                const res: ProcessSaleTransactionResult = {
                    isSuccess: false,
                    step: "payment",
                    prepareStandbyOrderResult: standbyOrderResult,
                    orderProcessingResult: err as OrderProcessingResult
                } as ProcessSaleTransactionResult;
                throw res;
            }

            if(this.firstTransactionResult.threeDSRequired) {
                await this.process3DSMethodCheck();
                
                const proceedWithPayment = await this.run3DSAuthentication(standbyOrderResult.encryptedUID, actualPaymentMethod);

                if(proceedWithPayment) {

                    try {
                        this.finalPmtResult = await processTransaction(this.mvcapi, standbyOrderResult.encryptedUID, actualPaymentMethod, this.sendMailAtTheEnd, this.firstTransactionResult.transactionID);

                        const res: ProcessSaleTransactionResult = {
                            isSuccess: true,
                            step: "payment",
                            prepareStandbyOrderResult: standbyOrderResult,
                            orderProcessingResult: this.finalPmtResult
                        };
                        this.resolveProcess(res);

                    } catch(err) {
                        const res: ProcessSaleTransactionResult = {
                            isSuccess: false,
                            step: "payment",
                            prepareStandbyOrderResult: standbyOrderResult,
                            orderProcessingResult: err as OrderProcessingResult
                        } as ProcessSaleTransactionResult;
                        throw res;
                    }

                    this.rejectProcess({
                        isSuccess: false,
                        step: "payment",
                        prepareStandbyOrderResult: standbyOrderResult,
                        orderProcessingResult: this.firstTransactionResult
                    });

                } else {
                    // Modify the first transaction result because this is what's used to propagate the error message.
                    this.firstTransactionResult.isSuccess = false;
                    this.firstTransactionResult.hasMessage = true;
                    this.firstTransactionResult.message = tr("Transaction refusée");
                    this.rejectProcess({
                        isSuccess: false,
                        step: "payment",
                        prepareStandbyOrderResult: standbyOrderResult,
                        orderProcessingResult: this.firstTransactionResult
                    });
                }

            } else {

                // No 3DS required and the payment is done. Let's notify
                const res: ProcessSaleTransactionResult = {
                    isSuccess: true,
                    step: "payment",
                    prepareStandbyOrderResult: standbyOrderResult,
                    orderProcessingResult: this.firstTransactionResult
                };
                this.resolveProcess(res);
            }

        } catch(err) {
            console.error("Error during process sale transaction", err);
            this.rejectProcess(err);
        }

        return resultPromise;
    }

    /**
     * Optional step to process 3DS Method Check, If it fails we still continue. It's just that the transaction will be more suspect.
     * @returns Worked on not
     */
    private async process3DSMethodCheck() {

        // If the URL is null, it means that the ACS doesn't not provide this service. We just skip it.
        if(this.firstTransactionResult.threeDSMethodUrl == null) {
            this.threeDSMethodURLDone = true;
            return Promise.resolve(false);
        }

        const resultProm = { resolve: null, reject: null}
        const resultPromise = new Promise<boolean>((resolve, reject) => {
            resultProm.resolve = resolve;
            resultProm.reject = reject;
        });

        try {
            var iFrame = await this.on3DSHiddenIFrameNeeded();

            // Create a timeout to exit from here if it takes too long. Global Payments suggest that we set a certain timeout to prevent transaction to take forever.
            this.threeDSMethodTimeout = setTimeout(() => {
                if(!this.threeDSMethodURLDone) {
                    console.error("3DS method check timed out. Moving to next step.");
                    this.on3DSHiddenIFrameDone();
                    this.threeDSMethodURLDone = true;
                    resultProm.resolve(false);
                } else {
                    console.error("3ds timeout ignored.");
                }
            }, THREEDS_METHOD_CHECK_TIMEOUT);

            // Watch the IFrame for when it's done
            iFrame.onload = () => {
                if(!this.threeDSMethodURLDone) {
                    let content = "";
                    if(iFrame.contentDocument) {
                        content = iFrame.contentDocument.body.innerHTML;
                    }

                    // We need to wait for the IFrame to contain the indication that it is done.
                    if(content.includes(RESERVATECH_3DS_DONE)) {
                        clearTimeout(this.threeDSMethodTimeout);

                        this.threeDSMethodURLDone = true;

                        // Call the removal of the IFrame
                        this.on3DSHiddenIFrameDone();

                        // Look for a result
                        const result = content.includes(RESERVATECH_3DS_OK);

                        resultProm.resolve(result);
                    }
                } /*else {
                    console.error("3DS Hidden IFrame already done. Ignoring the event.");
                }*/
            }

            // Execute the method check URL
            var form = document.createElement("form");
            form.setAttribute("method", "POST");
            form.setAttribute("action", this.firstTransactionResult.threeDSMethodUrl);
            form.setAttribute("target", iFrame.name);
            
            var threeDSMethodData = document.createElement("input");
            threeDSMethodData.setAttribute("type", "hidden");
            threeDSMethodData.setAttribute("name", "threeDSMethodData");
            threeDSMethodData.setAttribute("value", this.firstTransactionResult.threeDSMethodData);
            form.appendChild(threeDSMethodData);
            document.body.appendChild(form);
            form.submit();

        } catch(err) {
            resultProm.resolve(false);
        }
        
        return resultPromise;
    }

    async run3DSAuthentication(encTrID: string, paymentMethod: PaymentInfoModel): Promise<boolean> {
        let proceedWithPayment = false;
        const browserData = gatherBrowserData();
        
        //this.firstTransactionResult.transactionID

        try {
            this.threeDSAuthResult = await process3DSAuthentication(
                this.mvcapi,
                encTrID,
                this.firstTransactionResult.transactionID,
                browserData,
                paymentMethod,
                this.sendMailAtTheEnd
            );
        } catch(err) {
            this.threeDSAuthResult = err;
        }

        if(this.threeDSAuthResult.result == Process3DSAuthenticationResultValue.FRICTION_LESS) {
            // Friction less means that the transaction seems to be safe enough to proceed without any further action.
            proceedWithPayment = true;
        } else if(this.threeDSAuthResult.result == Process3DSAuthenticationResultValue.CHALLENGE_REQUIRED) {
            // Challenge means that the credit card issuer wants 
            proceedWithPayment = await this.run3DSChallenge();

        } else if (this.threeDSAuthResult.result == Process3DSAuthenticationResultValue.PAYMENT_REFUSED
            || this.threeDSAuthResult.result == Process3DSAuthenticationResultValue.TRANSACTION_FAILED_TO_COMPLETE) {
            proceedWithPayment = false;
        }

        return proceedWithPayment;
    }

    private async run3DSChallenge() {
        const chalReqInfo = this.threeDSAuthResult.challengeRequest;

        const resultProm = { resolve: null, reject: null}
        const resultPromise = new Promise<boolean>((resolve, reject) => {
            resultProm.resolve = resolve;
            resultProm.reject = reject;
        });

        try {
            // If the width and height are 0, we set them to 800x600 for the full screen
            // If we execute this code it is because we have a challenge should never have 0x0
            let iframeWidth = chalReqInfo.iframeWidth == 0 ? 800 : chalReqInfo.iframeWidth;
            let iframeHeight = chalReqInfo.iframeHeight == 0 ? 600 : chalReqInfo.iframeHeight;

            var iFrame = await this.on3DSChallengeIFrameNeeded(iframeWidth, iframeHeight);

            // Watch the IFrame for when it's done
            iFrame.onload = () => {
                if(!this.threeDSChallengeDone) {
                    let content = "";
                    if(iFrame.contentDocument) {
                        content = iFrame.contentDocument.body.innerHTML;
                    }

                    // We need to wait for the IFrame to contain the indication that it is done.
                    if(content.includes(RESERVATECH_3DS_DONE)) {
                        this.threeDSChallengeDone = true;

                        // Call the removal of the IFrame
                        this.on3DSChallengeIFrameDone();

                        // Look for a result
                        const result = content.includes(RESERVATECH_3DS_OK);

                        resultProm.resolve(result);
                    }
                } else {
                    console.error("3DS Challenge IFrame already done. Ignoring the event.");
                }
            }

            // Execute the challenge
            var form = document.createElement("form");
            form.setAttribute("method", "POST");
            form.setAttribute("action", this.threeDSAuthResult.challengeRequest.url);
            form.setAttribute("target", iFrame.name);
            
            var threeDSChallengeData = document.createElement("input");
            threeDSChallengeData.setAttribute("type", "hidden");
            threeDSChallengeData.setAttribute("name", "creq");
            threeDSChallengeData.setAttribute("value", this.threeDSAuthResult.challengeRequest.encodedData);
            form.appendChild(threeDSChallengeData);
            document.body.appendChild(form);
            form.submit();

        } catch(err) {
            console.error("Error during 3DS Challenge UI", err);
            resultProm.resolve(false);
        }
        
        return resultPromise;
    }
}

/**
 * Gather browser data (Function suggested by global payments)
 */
export function gatherBrowserData(): ThreeDSDeviceData {
    const browserTime = new Date();
    return {
        colorDepth: screen.colorDepth, // 24
        javaEnabled: navigator.javaEnabled ? navigator.javaEnabled() : false, // true
        browserLanguage: navigator.language, // en_US
        screenHeight: screen.height, // 1080
        screenWidth: screen.width, // 1920
        userAgent: navigator.userAgent,
        browserTime: browserTime,
        browserTimezoneZoneOffset: browserTime.getTimezoneOffset(), // 0 
    }
}


export type ProcessSaleTransactionResult = {
    isSuccess: boolean;
    step: "standby" | "payment";
    prepareStandbyOrderResult: PrepareStandByOrderResponseModel;
    orderProcessingResult: OrderProcessingResult;
}